import { useCallback, useMemo, useState } from "react";
import { api } from "../api/client";
import { usePoll } from "../hooks/usePoll";
import { SearchInput } from "../components/Table";
import { ActivityTable } from "../components/ActivityTable";
import {
  filterActivity,
  sortActivity,
  EMPTY_ACTIVITY_FILTERS,
  type ActivityFilters,
  type SortOrder,
} from "../lib/activity";
import type { ActivityRow } from "../api/types";

// The Live view (spec-8): the engagement-ranked activity board. One ranked table,
// busiest first by default (ac-11), replacing the old sign-ups / memexes feeds
// (ac-1). It polls GET /api/activity every ~10s (ac-6) and renders the FULL set
// client-side — agent-legible, emails untruncated, no pagination (ac-6). A
// Busiest⇄Most-recent toggle reorders the ALREADY-LOADED rows with NO refetch
// (ac-7/ac-24); a cohort filter, hide-internal (default ON, spec-4 parity) and a
// global search compose the view (ac-10). Per-row drill-downs: user → /users/:id,
// org → /orgs/:id (ac-3).

const COHORTS: { value: ActivityFilters["cohort"]; label: string }[] = [
  { value: "all", label: "All cohorts" },
  { value: "this_week", label: "This week" },
  { value: "last_week", label: "Last week" },
  { value: "this_month", label: "This month" },
  { value: "older", label: "Older" },
];

export function DashboardPage() {
  const [filters, setFilters] = useState<ActivityFilters>(EMPTY_ACTIVITY_FILTERS);
  const [order, setOrder] = useState<SortOrder>("busiest");

  // Stable fetcher — it depends on NOTHING that the controls change, so toggling
  // the sort order or any filter reorders/refilters the loaded rows in memory
  // without triggering a refetch (ac-7/ac-24).
  const fetcher = useCallback(() => api<{ users: ActivityRow[] }>("/api/activity"), []);
  const { data, error } = usePoll(fetcher);
  const rows = useMemo(() => data?.users ?? [], [data]);

  const set = <K extends keyof ActivityFilters>(key: K, value: ActivityFilters[K]) =>
    setFilters((f) => ({ ...f, [key]: value }));

  const visible = useMemo(
    () => sortActivity(filterActivity(rows, filters), order),
    [rows, filters, order],
  );

  return (
    <div className="mx-auto max-w-screen-2xl px-8 py-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl text-heading">Live</h1>
          <p className="text-sm text-muted">
            Who's engaged right now — ranked by recent activity, refreshing every 10s.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-edge p-0.5 text-sm" role="group" aria-label="Sort order">
            <OrderButton active={order === "busiest"} onClick={() => setOrder("busiest")} testid="order-busiest">
              Busiest
            </OrderButton>
            <OrderButton active={order === "recent"} onClick={() => setOrder("recent")} testid="order-recent">
              Most recent
            </OrderButton>
          </div>
          <select
            data-testid="cohort-filter"
            value={filters.cohort}
            onChange={(e) => set("cohort", e.target.value as ActivityFilters["cohort"])}
            className="rounded-lg border border-edge bg-surface px-2 py-1.5 text-sm text-primary"
          >
            {COHORTS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <SearchInput
            value={filters.search}
            onChange={(v) => set("search", v)}
            placeholder="Search name, email, org, tag…"
          />
          <label className="flex items-center gap-1.5 text-sm text-secondary">
            <input
              type="checkbox"
              data-testid="hide-internal"
              checked={filters.hideInternal}
              onChange={(e) => set("hideInternal", e.target.checked)}
            />
            Hide internal
          </label>
        </div>
      </header>

      <p className="mb-2 text-sm text-muted">
        {visible.length} of {rows.length} users
      </p>

      {error && <p className="mb-2 text-sm text-status-danger-text">Failed to refresh — showing the last good data.</p>}

      <ActivityTable rows={visible} />
    </div>
  );
}

function OrderButton({
  active,
  onClick,
  testid,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-md px-3 py-1 transition-colors ${
        active ? "bg-card-hover font-medium text-heading" : "text-secondary hover:text-primary"
      }`}
    >
      {children}
    </button>
  );
}
